import React from 'react';
import { Plane, Map, Compass, Package, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { diensten } from '../mockData';

const Diensten = () => {
  const iconMap = {
    Plane: Plane,
    Map: Map,
    Compass: Compass,
    Package: Package
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1535262412227-85541e910204?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHx0cm9waWNhbCUyMGJlYWNofGVufDB8fHx8MTc3NjA5NzQzOXww&ixlib=rb-4.1.0&q=85"
            alt="Diensten"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/85 via-blue-900/75 to-blue-800/70" />
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Onze Diensten
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            Alles wat je nodig hebt voor jouw perfecte reis
          </p>
        </div>
      </section>

      {/* Diensten Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {diensten.map((dienst) => {
              const IconComponent = iconMap[dienst.icon];
              return (
                <div
                  key={dienst.id}
                  className="group p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-200 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">{dienst.titel}</h3>
                  <p className="text-gray-600 leading-relaxed text-lg">{dienst.beschrijving}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reizen op Maat Section */}
      <section className="py-20 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-blue-200 mb-6">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-medium">Uniek & Persoonlijk</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Reizen Volledig op Maat
                </h2>
                <p className="text-xl text-blue-100 leading-relaxed mb-8">
                  Elke reis die we samenstellen is uniek. We luisteren naar jouw wensen, begrijpen jouw reisstijl en creëren een persoonlijk reisplan dat perfect bij jou past.
                </p>
                <ul className="space-y-4 mb-8">
                  {[
                    "Persoonlijk reisadvies op basis van jouw voorkeuren",
                    "Flexibele planning die past bij jouw budget",
                    "Handgeselecteerde accommodaties en activiteiten",
                    "24/7 ondersteuning tijdens je reis"
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <ArrowRight className="w-4 h-4" />
                      </div>
                      <span className="text-blue-100">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/rondreizen-tool"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-900 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  Start je Reis
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1603477849227-705c424d1d80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMGJlYWNofGVufDB8fHx8MTc3NjA5NzQzOXww&ixlib=rb-4.1.0&q=85"
                    alt="Reizen op Maat"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-blue-300/20 rounded-full blur-3xl" />
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-400/20 rounded-full blur-3xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rondreizen Tool Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHx0cm9waWNhbCUyMGJlYWNofGVufDB8fHx8MTc3NjA5NzQzOXww&ixlib=rb-4.1.0&q=85"
                    alt="Rondreizen Planning"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-900 mb-6">
                  <Map className="w-4 h-4" />
                  <span className="text-sm font-medium">Interactieve Planning</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6">
                  Rondreizen Planning Tool
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed mb-8">
                  Gebruik onze interactieve tool om je eigen rondreis samen te stellen. Kies bestemmingen, bepaal reisduur, selecteer activiteiten en accommodaties - allemaal op één plek.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    "Kies uit tientallen prachtige bestemmingen",
                    "Bepaal zelf je reisduur en tempo",
                    "Selecteer activiteiten die bij jou passen",
                    "Kies accommodaties voor elk budget"
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <ArrowRight className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/rondreizen-tool"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white font-semibold rounded-lg hover:from-blue-800 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  Probeer de Tool
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-900 to-blue-700 rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full blur-3xl" />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Niet Zeker Welke Dienst Je Nodig Hebt?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Geen probleem! Neem contact met ons op en we helpen je graag verder
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-900 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              >
                Neem Contact Op
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Diensten;
